import React, { Component } from "react";
import { Table, Input, Space, Button, Checkbox } from "antd";
import { SearchOutlined, ClearOutlined, UserSwitchOutlined } from "@ant-design/icons";
import locale from "./localeTable";
import './Styles.css';
import ButtonItem from "./ButtonItem";
import EstadosLeyenda from "./EstadosLeyenda";
import moment from "moment";
moment.locale("es");

// Preferencia de "en qué campos busca el buscador". Se guarda aparte de
// filterStorageKey a propósito: aquel blob caduca a los 10 minutos, lo cual está
// bien para filtros de sesión y mal para una preferencia que debe quedarse pegada.
//
// Van como funciones de módulo (y no como métodos) porque el state las llama
// durante su propia inicialización, antes de que existan las class properties.
const cargarCamposBusquedaGuardados = (config) => {
    if (!config?.storageKey) return [];
    try {
        const guardado = localStorage.getItem(config.storageKey);
        if (!guardado) return [];
        const campos = JSON.parse(guardado);
        if (!Array.isArray(campos)) return [];
        // Se valida contra lo que declara la página: si mañana se quita un campo
        // del selector, lo que quedó guardado deja de aplicar solo.
        const permitidos = (config.opcionales || []).map((opcion) => opcion.campo);
        return campos.filter((campo) => permitidos.includes(campo));
    } catch (error) {
        console.error("Error al cargar campos de búsqueda guardados:", error);
        return [];
    }
};

const guardarCamposBusquedaGuardados = (config, campos) => {
    if (!config?.storageKey) return;
    try {
        localStorage.setItem(config.storageKey, JSON.stringify(campos));
    } catch (error) {
        console.error("Error al guardar campos de búsqueda:", error);
    }
};


class TableItemDinamico extends Component {
    state = {
        searchText: "",
        searchedColumn: "",
        // Estados para controlar la paginación y filtros del servidor
        pagination: {
            current: 1,
            pageSize: 100,
            total: 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} elementos`,
        },
        sorter: {},
        filters: {},
        externalFilters: {}, // Filtros externos (como Segmented)
        currentFilters: {},
        // Campos OPCIONALES activos del buscador. El campo fijo no vive aquí: se
        // agrega al mandar. Se carga sincrónicamente para que la primera consulta
        // tras recargar la página ya salga con la preferencia del usuario.
        camposBusquedaExtra: cargarCamposBusquedaGuardados(this.props.camposBusqueda),
    };

    cargarFiltrosGuardados = () => {
        if (!this.props.filterStorageKey) return {};

        try {
            const savedData = localStorage.getItem(this.props.filterStorageKey);
            if (!savedData) return {};

            const { filtros, timestamp } = JSON.parse(savedData);

            // Verificar si han pasado 10 minutos (600000 ms)
            const ahora = Date.now();
            const tiempoTranscurrido = ahora - timestamp;
            const DIEZ_MINUTOS = 10 * 60 * 1000; // 600000 ms

            if (tiempoTranscurrido >= DIEZ_MINUTOS) {
                // Si pasaron 10 minutos, eliminar y retornar objeto vacío
                localStorage.removeItem(this.props.filterStorageKey);
                return {};
            }

            return filtros;
        } catch (error) {
            console.error('Error al cargar filtros guardados:', error);
            return {};
        }
    };

    guardarFiltros = (filtros) => {
        if (!this.props.filterStorageKey) return;
        try {
            localStorage.setItem(this.props.filterStorageKey, JSON.stringify({
                filtros,
                timestamp: Date.now(),
            }));
        } catch (error) {
            console.error('Error al guardar filtros:', error);
        }
    };

    normalizeFilters = (filters) => {
        const result = {};
        Object.keys(filters).forEach(key => {
            if (Array.isArray(filters[key])) {
                result[key] = filters[key].flat(Infinity);
            } else {
                result[key] = filters[key];
            }
        });
        return result;
    }

    // Función para obtener datos del servidor
    fetchData = (params = {}) => {
        const { pagination, sorter, filters, externalFilters } = this.state;
        // const normalizedFilters = this.normalizeFilters({ ...filters, ...params.filters });
        const normalizedFilters = { ...filters, ...params.filters };
        const requestBody = {
            //page: pagination.current || params.page || 1,
            pageSize: params.pageSize || pagination.pageSize,
            sortField: params.sortField || sorter.field,
            sortOrder: params.sortOrder || sorter.order,
            ...externalFilters,
            ...params.externalFilters,
            filters: normalizedFilters,
        };
        requestBody.page = params.page || pagination.current || 1;
        requestBody.sortOrder = params.sortOrder || (sorter.order ? sorter.order : undefined);
        requestBody.sortField = params.sortField || (sorter.field ? sorter.field : undefined);

        // El campo fijo siempre va; el backend igual lo revalida contra su lista
        // blanca, así que esto es conveniencia, no la garantía.
        if (this.props.camposBusqueda) {
            requestBody.camposBusqueda = [
                this.props.camposBusqueda.fijo.campo,
                ...this.state.camposBusquedaExtra,
            ];
        }


        const filtrosCombinados = {
            ...externalFilters,
            ...params.externalFilters,
            filters: normalizedFilters,
            sorter: {
                field: params.sortField || sorter.field,
                order: params.sortOrder || sorter.order,
            },
            pagination: {
                page: pagination.current || params.page,
                pageSize: params.pageSize || pagination.pageSize,
            }
        };

        // Guardar filtros si está habilitado
        this.guardarFiltros(filtrosCombinados);

        // Actualizar estado con filtros actuales
        this.setState({
            currentFilters: filtrosCombinados
        });

        // Llamar a la función que recibe como prop para obtener datos
        if (this.props.onFetchData) {
            this.props.onFetchData(requestBody);
        }
    };

    // Método para actualizar filtros externos (llamado desde el componente padre)
    updateExternalFilters = (newExternalFilters) => {
        this.setState({
            externalFilters: newExternalFilters,
            pagination: { ...this.state.pagination, current: 1 }, // Resetear a página 1
        });

        this.fetchData({
            page: 1,
            externalFilters: newExternalFilters,
        });
    };

    restaurarFiltrosGuardados = () => {
        const filtrosGuardados = this.cargarFiltrosGuardados();

        // Validar que filtrosGuardados no sea null, undefined o vacío
        if (filtrosGuardados && Object.keys(filtrosGuardados).length > 0) {
            // Restaurar estado desde filtros guardados
            const newState = {
                externalFilters: {
                    ...filtrosGuardados,
                    filters: undefined, // Remover filters del nivel superior
                    sorter: undefined,
                    pagination: undefined,
                },
                filters: filtrosGuardados.filters || {},
                sorter: filtrosGuardados.sorter || {},
                pagination: {
                    ...this.state.pagination,
                    current: filtrosGuardados.pagination?.page || 1,
                    pageSize: filtrosGuardados.pagination?.pageSize || 100,
                },
                currentFilters: filtrosGuardados,
            };

            // Si valor esta guardado pasar a props
            if (filtrosGuardados.valor) {
                this.setValor(filtrosGuardados.valor);
            }

            if (filtrosGuardados.textoFiltro) {
                this.setTextoFiltro(filtrosGuardados.textoFiltro);
            }

            this.setState(newState, () => {
                // Forzar actualización de la tabla para que muestre los filtros
                this.forceUpdate();

                // Delay para asegurar que el estado se actualice antes de hacer la consulta
                setTimeout(() => {
                    this.fetchData({
                        page: filtrosGuardados.pagination?.page || 1,
                        pageSize: filtrosGuardados.pagination?.pageSize || 100,
                        sortField: filtrosGuardados.sorter?.field,
                        sortOrder: filtrosGuardados.sorter?.order,
                        externalFilters: {
                            ...filtrosGuardados,
                            filters: undefined,
                            sorter: undefined,
                            pagination: undefined,
                        },
                        filters: filtrosGuardados.filters || {},
                    });
                }, 100);
            });
        } else {
            // Si no hay filtros guardados, cargar con filtros por defecto
            this.cargarFiltrosIniciales();
        }
    };

    //Regresar valor al component
    setValor = (valor) => {
        if (this.props.setValor) this.props.setValor(valor);
    }

    setTextoFiltro = (textoFiltro) => {
        if (this.props.setTextoFiltro) this.props.setTextoFiltro(textoFiltro);
    }

    toggleCampoBusqueda = (campo, activo) => {
        const camposBusquedaExtra = activo
            ? [...this.state.camposBusquedaExtra, campo]
            : this.state.camposBusquedaExtra.filter((valor) => valor !== campo);

        guardarCamposBusquedaGuardados(this.props.camposBusqueda, camposBusquedaExtra);

        this.setState({ camposBusquedaExtra }, () => {
            // Sólo tiene sentido volver a consultar si hay texto escrito: con la
            // caja vacía el campo elegido no cambia el resultado.
            if (this.state.externalFilters?.textoFiltro) {
                this.fetchData({ page: 1 });
            }
        });
    }

    cargarFiltrosIniciales = (valorDefault) => {
        let defaultSort = this.props.defaultSort || {};
        let defaultPageSize = this.props.defaultPageSize || 100;

        this.props.columns.forEach((column) => {
            if (column.defaultSortOrder) {
                defaultSort = {
                    field: column.dataIndex,
                    order: column.defaultSortOrder,
                };
            }
        });

        const externalFilters = this.props.anadirValorFiltro ?
            { valor: (valorDefault || valorDefault === 0 ? valorDefault : this.props.anadirValorFiltro) } : {};

        this.setState({
            sorter: defaultSort,
            externalFilters: externalFilters,
            pagination: {
                ...this.state.pagination,
                pageSize: defaultPageSize,
            },
        }, () => {
            this.fetchData({
                sortField: defaultSort.order ? defaultSort.field : undefined,
                sortOrder: defaultSort.order ? defaultSort.order : undefined,
                externalFilters: externalFilters,
            });
        });
    };

    // Exponer el método para que el componente padre pueda llamarlo
    componentDidMount() {
        // Si hay una clave de almacenamiento, intentar cargar filtros guardados
        if (this.props.filterStorageKey) {
            this.restaurarFiltrosGuardados();
        } else {
            this.cargarFiltrosIniciales();
        }

        // Exponer método al componente padre a través de ref
        if (this.props.onRef) {
            this.props.onRef(this);
        }
    }

    componentWillUnmount() {
        // Limpiar timeout del debounce
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        if (this.props.onRef) {
            this.props.onRef(null);
        }
    }

    // Método para ordenar desde el componente padre (campos calculados, menús personalizados, etc.)
    setSorter = (field, order) => {
        const sorter = field && order ? { field, order } : {};
        this.setState({
            sorter,
            pagination: { ...this.state.pagination, current: 1 },
        }, () => {
            this.fetchData({ page: 1 });
        });
    };

    // Manejar cambios en la tabla (paginación, ordenamiento, filtros)
    handleTableChange = (pagination, filters, sorter, extra) => {

        // Actualizar estado inmediatamente
        const newPagination = {
            ...this.state.pagination,
            current: pagination.current,
            pageSize: pagination.pageSize
        };

        // Al paginar, conservar el orden y filtros actuales (el orden pudo
        // establecerse con setSorter y antd no lo conoce)
        if (extra?.action === 'paginate') {
            this.setState({ pagination: newPagination }, () => {
                this.fetchData({
                    page: pagination.current,
                    pageSize: pagination.pageSize,
                });
            });
            return;
        }

        const newSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        //Agregar aqui el onlyMine si es que existe
        const newFilters = {
            onlyMine: this.state.filters?.onlyMine
        };

        // Procesar filtros
        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key].length > 0) {
                newFilters[key] = filters[key];
            }
        });

        // Actualizar estado
        this.setState({
            pagination: newPagination,
            sorter: {
                field: newSorter.order ? newSorter.field : undefined,
                order: newSorter.order ? newSorter.order : undefined,
            },
            filters: newFilters,
        }, () => {
            // Llamar fetchData con los nuevos valores
            this.fetchData({
                page: pagination.current,
                pageSize: pagination.pageSize,
                sortField: newSorter.order ? newSorter.field : undefined,
                sortOrder: newSorter.order ? newSorter.order : undefined,
                filters: newFilters,
                externalFilters: this.state.externalFilters,
            });
        });
    };

    // Props de búsqueda para columnas (modificado para servidor)
    getColumnSearchProps = (dataIndex, render) => {
        // Soportar búsqueda en múltiples campos
        const searchFields = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
       // const primaryField = searchFields[0];

        // Obtener el valor actual de cualquiera de los campos
        const getCurrentValue = () => {
            for (const field of searchFields) {
                if (this.state.filters[field]) {
                    return this.state.filters[field];
                }
            }
            return '';
        };

        return {
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => {
                // Usar el valor del filtro guardado si existe
                const currentValue = selectedKeys[0] !== undefined ? selectedKeys[0] : getCurrentValue();

                return (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={(node) => {
                                this.searchInput = node;
                            }}
                            placeholder={`Buscar...`}
                            value={currentValue}
                            onChange={(e) =>
                                setSelectedKeys([e.target.value])
                            }
                            onPressEnter={() =>
                                this.handleSearch(selectedKeys, confirm, dataIndex)
                            }
                            style={{ width: 250, marginBottom: 8, display: "block" }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => this.handleSearch(selectedKeys, confirm, dataIndex)}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Buscar
                            </Button>
                            <Button
                                onClick={() => this.handleReset(clearFilters, dataIndex)}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Limpiar
                            </Button>
                        </Space>
                    </div>
                );
            },
            filterIcon: (filtered) => {
                const isFiltered = filtered || searchFields.some(field => this.state.filters[field]);
                return (
                    <SearchOutlined style={{ color: isFiltered ? "#1890ff" : undefined }} />
                );
            },
            // Establecer el valor filtrado desde el estado
            filteredValue: getCurrentValue() ? [getCurrentValue()] : null,
            // Remover onFilter ya que el filtrado se hace en el servidor
            onFilterDropdownVisibleChange: (visible) => {
                if (visible) {
                    setTimeout(() => this.searchInput.select(), 100);
                }
            },
        };
    };

    // Manejar búsqueda (modificado para servidor y múltiples campos)
    handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();

        const searchFields = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
        const newFilters = { ...this.state.filters };

        searchFields.forEach(field => {
            newFilters[field] = selectedKeys[0] || undefined;
        });

        // Limpiar filtros vacíos
        Object.keys(newFilters).forEach(key => {
            if (!newFilters[key]) {
                delete newFilters[key];
            }
        });

        this.setState({
            searchText: selectedKeys[0],
            searchedColumn: Array.isArray(dataIndex) ? dataIndex[0] : dataIndex,
            filters: newFilters,
            pagination: { ...this.state.pagination, current: 1 }, // Resetear a página 1
        });
    };

    // Manejar reset de búsqueda (modificado para servidor y múltiples campos)
    handleReset = (clearFilters, dataIndex) => {
        clearFilters();

        const searchFields = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
        const newFilters = { ...this.state.filters };

        // Eliminar filtros de todos los campos de búsqueda
        searchFields.forEach(field => {
            delete newFilters[field];
        });

        this.setState({
            searchText: "",
            filters: newFilters,
            pagination: { ...this.state.pagination, current: 1 }, // Resetear a página 1
        });
    };

    clearAllFilters = () => {
        this.setState({
            filters: {},
            externalFilters: {},
            pagination: { ...this.state.pagination, current: 1 },
            currentFilters: {},
        });

        // Limpiar filtros guardados
        if (this.props.filterStorageKey) {
            localStorage.removeItem(this.props.filterStorageKey);
        }

        if (this.props.setValor && (this.props.valorDefault || this.props.valorDefault === 0)) {
            this.props.setValor(this.props.valorDefault);
        }
        if (this.props.valorTextoDefault || this.props.valorTextoDefault === "") {
            this.props.setTextoFiltro(this.props.valorTextoDefault)
        }
    };

    // Actualizar paginación cuando se reciben nuevos datos
    // MÉTODO ACTUALIZADO: Actualizar paginación cuando se reciben nuevos datos
    componentDidUpdate(prevProps) {
        if (prevProps.totalRecords !== this.props.totalRecords) {

            const newTotal = this.props.totalRecords || 0;
            const currentPage = this.state.pagination.current;
            const pageSize = this.state.pagination.pageSize;

            const maxPages = Math.ceil(newTotal / pageSize);
            const validPage = Math.min(currentPage, Math.max(1, maxPages));

            this.setState({
                pagination: {
                    ...this.state.pagination,
                    total: newTotal,
                    current: validPage,
                }
            });
        }

        if (prevProps.dataSource !== this.props.dataSource) {
            const currentData = this.props.dataSource || [];
            const currentPage = this.state.pagination.current;
            const pageSize = this.state.pagination.pageSize;
            const totalRecords = this.props.totalRecords || 0;

            const maxPages = Math.ceil(totalRecords / pageSize);

            if (currentData.length === 0 && totalRecords > 0 && currentPage > maxPages) {
                const validPage = Math.max(1, maxPages);
                this.setState({
                    pagination: {
                        ...this.state.pagination,
                        current: validPage,
                    }
                }, () => {
                    this.fetchData({ page: validPage });
                });
            }
        }
    }


    toggleOnlyMine = (e) => {
        const current = !!this.state.filters?.onlyMine;
        const newFilters = { ...this.state.filters, onlyMine: !current };
        // reset a página 1 para que haga sentido al usuario
        this.setState({
            filters: newFilters,
            pagination: { ...this.state.pagination, current: 1 },
        }, () => {
            this.fetchData({
                page: 1,
                filters: newFilters,
            });
        });
    };

    handleTextoFiltroChange = (value) => {

        this.setState(prevState => ({
            externalFilters: { ...prevState.externalFilters, textoFiltro: value }
        }));

        if (this.props.setTextoFiltro) {
            this.props.setTextoFiltro(value);
        }

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.fetchData({
                page: 1,
                externalFilters: { ...this.state.externalFilters, textoFiltro: value },
            });
        }, 500);
    };

    // Wrapper para convertir columnas de acción - Usa acciones-container
    wrapActionsColumn = (column) => {
        if (!column.render) return column;

        const originalRender = column.render;

        return {
            ...column,
            render: (value, record, index) => {
                const result = originalRender(value, record, index);

                if (React.isValidElement(result) && result.type === Space) {
                    const buttons = React.Children.toArray(result.props.children).filter(Boolean);

                    // Si no hay botones, no mostrar nada
                    if (buttons.length === 0) {
                        return null;
                    }

                    // Retornar los botones en el nuevo contenedor
                    return (
                        <div className="acciones-container">
                            {buttons}
                        </div>
                    );
                }

                return result;
            }
        };
    };


    obtenerClaseVencimiento = (record) => {
        const { estadosLeyenda } = this.props;
        if (!estadosLeyenda || estadosLeyenda.length === 0 || !record.createdAt) {
            return null;
        }

        if(record.estado === 3 || record.estado === 7) return null;

        const hoy = moment().startOf('day');
        
        if (record.fechaVigencia) {
            const fechaVigencia = moment(record.fechaVigencia).startOf('day');
            if (hoy.isAfter(fechaVigencia)) {
                return null; // Ya venció, se queda con el color default (o el de estado vencido si tienes otro)
            }
        }
        
        const fechaCreacion = moment(record.createdAt).startOf('day');
        // Diff en días: Si se creó el 10 y hoy es 15, son 5 días.
        const diasAntiguedad = hoy.diff(fechaCreacion, 'days');


        for (let i = 0; i < Math.min(estadosLeyenda.length, 3); i++) {
            const rango = estadosLeyenda[i];
            
            if (rango.tope === -1 || diasAntiguedad <= rango.tope) {
                return `rango-dinamico-${i}`; // Retorna la clase que usa la variable CSS
            }
        }
        return null;
    };

    obtenerClaseSemaforoVigencia = (record) => {
        if (!record.fechaVigencia) return null;
        if (record.estado === 3 || record.cerrado || record.finiquitado) return null;

        const hoy = moment().startOf('day');
        const vigencia = moment(record.fechaVigencia).startOf('day');
        const diasRestantes = vigencia.diff(hoy, 'days');

        if (diasRestantes < 0) return 'alta-importancia';
        if (diasRestantes <= 15) return 'media-importancia';
        return 'baja-importancia';
    };

    render() {
        const { pagination } = this.state;

        let columns = this.props.columns.map((value) => {
            let newVal = { ...value };
            if (value.buscar) {
                newVal = { ...value, ...this.getColumnSearchProps(value.buscar) };
            }

            // Controlar las flechas de orden desde el estado, para que siempre
            // reflejen el orden real (incluye el establecido con setSorter o restaurado)
            if (value.sorter && value.sortOrder === undefined) {
                newVal.sortOrder = this.state.sorter?.field === value.dataIndex
                    ? (this.state.sorter.order || null)
                    : null;
            }

            // Procesar sub-columnas (children) si existen
            if (value.children && Array.isArray(value.children)) {
                newVal.children = value.children.map((child) => {
                    let newChild = { ...child };
                    if (child.buscar) {
                        newChild = { ...child, ...this.getColumnSearchProps(child.buscar) };
                    }
                    return newChild;
                });
            }

            // Aplicar wrapper a columnas de acción/acciones
            if (value.dataIndex === 'accion' || value.dataIndex === 'acciones' || value.key === 'accion' || value.key === 'acciones') {
                newVal = this.wrapActionsColumn(newVal);
            }

            return newVal;
        });

        const isMobile = window.innerWidth <= 1000;

        return (
            <Table
                className="custom-small-table"
                locale={{ ...locale, ...this.props.locale }}
                // title={() => (
                //     <div style={{ display: "flex", justifyContent: "flex-end" }}>
                //         <Button
                //             type="link"
                //             size="small"
                //             onClick={() => {
                //                 this.clearAllFilters();
                //                 this.cargarFiltrosIniciales();
                //             }}
                //         >
                //             Limpiar filtros
                //         </Button>
                //     </div>
                // )}
                title={() => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            padding: "0",
                            background: "transparent",
                            borderBottom: "none",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: "#f5f5f5",
                                borderRadius: "6px 6px 0 0",
                                padding: "6px 12px",
                                boxShadow: "0 -1px 3px rgba(0,0,0,0.1)",
                            }}
                        >
                            {this.props.estadosLeyenda && this.props.estadosLeyenda.length > 0 && (
                                <EstadosLeyenda estados={this.props.estadosLeyenda} compact />
                            )}
                            {this.props.setTextoFiltro && this.props.camposBusqueda && (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <Checkbox checked disabled style={{ whiteSpace: "nowrap" }}>
                                        {this.props.camposBusqueda.fijo.etiqueta}
                                    </Checkbox>
                                    {(this.props.camposBusqueda.opcionales || []).map((opcion) => (
                                        <Checkbox
                                            key={opcion.campo}
                                            checked={this.state.camposBusquedaExtra.includes(opcion.campo)}
                                            onChange={(e) => this.toggleCampoBusqueda(opcion.campo, e.target.checked)}
                                            style={{ whiteSpace: "nowrap", marginLeft: 0 }}
                                        >
                                            {opcion.etiqueta}
                                        </Checkbox>
                                    ))}
                                    <span style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>
                                        menos campos = más rápido
                                    </span>
                                </div>
                            )}
                            {this.props.setTextoFiltro && (
                                <Input
                                    ref={(node) => {
                                        this.searchInput = node;
                                    }}
                                    placeholder="Buscar..."
                                    value={this.state.externalFilters?.textoFiltro || ""}
                                    onChange={(e) => this.handleTextoFiltroChange(e.target.value)}
                                    style={{ width: 200, marginRight: 8 }}
                                    prefix={<SearchOutlined />}
                                    size="small"
                                />
                            )}
                            {this.props.onlyMineFilter && (
                                <ButtonItem
                                    type={this.state.filters?.onlyMine ? "primary" : "default"}
                                    size="small"
                                    onClick={this.toggleOnlyMine}
                                    icon={<UserSwitchOutlined />}
                                    blanco={!this.state.filters?.onlyMine}
                                />
                            )}
                            <ButtonItem
                                type="default"
                                size="small"
                                blanco
                                icon={<ClearOutlined />}
                                onClick={() => {
                                    this.clearAllFilters();
                                    this.cargarFiltrosIniciales(this.props.valorDefault);
                                    if (this.props.onClearFilters) this.props.onClearFilters();
                                }}
                            >
                                Limpiar Filtros
                            </ButtonItem>
                        </div>
                    </div>
                )}

                columns={columns.map(valueColum => {
                    let ellipsis = valueColum.dataIndex !== 'accion'
                    if (valueColum.ellipsis === false) ellipsis = false

                    if (valueColum.dataIndex === 'subtotal' || valueColum.dataIndex === 'total' ||
                        valueColum.dataIndex === 'saldo' || valueColum.dataIndex === 'costo' ||
                        valueColum.dataIndex === 'totalPagar' || valueColum.dataIndex === 'cantidad' ||
                        valueColum.dataIndex === 'cargos' || valueColum.dataIndex === 'abonos') {
                        return { ...valueColum, ellipsis, align: 'right' }
                    } else {
                        return { ...valueColum, ellipsis }
                    }
                })}
                dataSource={this.props.dataSource}
                rowSelection={this.props.rowSelection}
                onRow={this.props.onRow}
                components={this.props.components}
                loading={this.props.loading}
                size={this.props.size}
                expandable={this.props.expandable}
                rowClassName={this.props.rowClassName ? this.props.rowClassName : (record, index) => {
                    const claseVencimiento = this.obtenerClaseVencimiento(record);
                    if (claseVencimiento) return claseVencimiento;
                    if (this.props.semaforoVigencia) {
                        const claseSemaforoVigencia = this.obtenerClaseSemaforoVigencia(record);
                        if (claseSemaforoVigencia) return claseSemaforoVigencia;
                    }
                    if(this.props?.portalClasses){
                        if(!record?.estadoPortal || record.estadoPortal === 0){
                            return 'sin-subir'
                        }
                        else if(record?.estadoPortal === 1){
                            return 'pendiente-revisando'
                        }
                        else if(record?.estadoPortal === 2){
                            return 'subido'
                        }

                    }

                    if (record.llegoMaterial) {
                        return 'llego-material'
                    }
                    if (record.apartado && (record.estado === 1 || record.estado === 2)) {
                        return 'apartado'
                    }
                    if (record.efo) {
                        return 'efo'
                    }
                    if (record.tieneExtras) {
                        return 'tiene-extras'
                    }
                    if (record.importancia === 3) {
                        return 'alta-importancia'
                    }
                    if (record.importancia === 2) {
                        return 'media-importancia'
                    }
                    if (record.importancia === 1) {
                        return 'baja-importancia'
                    }
                    if (record.clienteEcommerce) {
                        return 'cliente-ecommerce'
                    }
                    if (this.props.rowClassName) {
                            return this.props.rowClassName(record, index);
                        }
                    // Solo aplicar zebra-gris si no hay otros estados especiales
                    if (index % 2 === 0) {
                        return 'zebra-gris'
                    }
                    return 'default'
                }}

                rowKey={this.props.rowKey ? this.props.rowKey : (value) => value._id}
                tableLayout='fixed'
                scroll={{ x: isMobile ? "max-content" : undefined, y: '60vh' }}
                // Configuración de paginación del servidor
                pagination={{
                    ...pagination,
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: this.props.totalRecords || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} elementos`,
                }}
                // Manejar cambios en la tabla
                onChange={this.handleTableChange}
            />
        );
    }
}

export default TableItemDinamico;